import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import ReviewSection from "@/pages/detail/components/ReviewSection";
import { works } from "@/data/catalog";
import { formatViews, getRelated, getWorkById } from "@/utils/workUtils";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-3 py-2.5 border-b border-background-200 last:border-0">
      <span className="w-16 shrink-0 text-sm text-foreground-500">{label}</span>
      <span className="text-sm text-foreground-900 flex-1">{value}</span>
    </div>
  );
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = useMemo(() => getWorkById(works, id ?? ""), [id]);
  const related = useMemo(() => (work ? getRelated(works, work, 4) : []), [work]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useDocumentMeta({
    title: work ? `${work.title} - 韩国伦理影视详细介绍及影评` : "作品不存在 - 韩国伦理",
    description: work
      ? `${work.title}（${work.year}）韩国伦理${work.type === "movie" ? "电影" : "电视剧"}剧情介绍、基本信息与影评精选，包含${work.genres.join("、")}等题材内容。`
      : "该作品不存在或已下架，返回韩国伦理首页浏览更多伦理影视作品。",
    keywords: work
      ? `${work.title},韩国伦理,${work.genres.join(",")},韩国伦理${work.type === "movie" ? "电影" : "电视剧"}`
      : "韩国伦理,伦理影视",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: work?.title ?? "韩国伦理", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      showToast("页面链接已复制");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!work) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20">
        <div className="max-w-md mx-auto rounded-lg border border-background-200 bg-background-100/50 p-8 text-center flex flex-col items-center gap-4">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-950">作品不存在或已下架</h1>
          <p className="text-sm text-foreground-600">
            你访问的作品可能已下架或链接有误，可以返回首页，或前往分类页继续浏览其他伦理影视作品。
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-1">
            <Link
              to="/"
              className="px-5 h-10 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
            <Link
              to="/category/erotic"
              className="px-5 h-10 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 transition-colors"
            >
              浏览分类
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const favorite = isFavorite(work.id);
  const categoryLink = work.type === "movie" ? "/category/erotic" : "/category/drama";
  const categoryLabel = work.type === "movie" ? "伦理电影" : "伦理电视剧";

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-24 lg:pb-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: categoryLabel, to: categoryLink },
          { label: work.title },
        ]}
      />

      <header className="mt-5 mb-6 md:mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-800 text-xs font-medium">
            {categoryLabel}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
            {work.year}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-semibold flex items-center gap-1">
            <i className="ri-star-fill text-[10px]" /> {work.rating.toFixed(1)}
          </span>
          <span className="text-xs text-foreground-500 flex items-center gap-1 ml-1">
            <i className="ri-eye-line" /> {formatViews(work.views)}次浏览
          </span>
        </div>
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
          {work.title}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {work.genres.map((genre) => (
            <Link
              key={genre}
              to={categoryLink}
              className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-700 transition-colors"
            >
              {genre}
            </Link>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-10">
        <div className="flex flex-col gap-5">
          <div className="w-full max-w-[320px] lg:max-w-none aspect-[2/3] rounded-lg overflow-hidden border border-background-200 bg-background-200 mx-auto lg:mx-0">
            <img
              src={work.poster}
              alt={`${work.title}海报`}
              title={`${work.title} 韩国伦理影视`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(work.id)}
              className={`flex-1 h-11 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap ${
                favorite
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
              {favorite ? "已收藏" : "收藏作品"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="h-11 px-4 rounded-md border border-background-300 text-foreground-700 text-sm font-medium flex items-center justify-center gap-2 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line" /> 分享
            </button>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/50 px-5 py-3">
            <h2 className="font-heading font-semibold text-foreground-950 py-2.5 border-b border-background-200">
              基本信息
            </h2>
            <InfoRow label="导演" value={work.director} />
            {work.cast.length > 0 && <InfoRow label="主演" value={work.cast.join(" / ")} />}
            <InfoRow label="类型" value={work.genres.join(" · ")} />
            <InfoRow label="上映年份" value={work.year} />
            {work.type === "movie" && work.duration && <InfoRow label="影片时长" value={work.duration} />}
            {work.type === "drama" && work.episodes && (
              <InfoRow label="集数" value={`全 ${work.episodes} 集`} />
            )}
            <InfoRow label="评分" value={`${work.rating.toFixed(1)} 分（演示评分）`} />
            <InfoRow
              label="标签"
              value={
                <span className="flex flex-wrap gap-1.5">
                  {work.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[11px] px-2 py-0.5 rounded bg-secondary-100 text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </span>
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-8">
          <section>
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">剧情简介</h2>
            <div className="flex flex-col gap-3">
              {work.synopsis.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base text-foreground-700 leading-7">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          <ReviewSection reviews={work.reviews} />

          {related.length > 0 && (
            <section>
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">推荐相关内容</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {related.map((item) => (
                  <WorkCard key={item.id} work={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-background-50 border-t border-background-200">
        <div className="px-4 h-16 flex items-center gap-3">
          <button
            type="button"
            onClick={handleShare}
            className="h-11 px-4 rounded-md border border-background-300 text-foreground-700 text-sm font-medium flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-forward-line" /> 分享
          </button>
          <button
            type="button"
            onClick={() => toggleFavorite(work.id)}
            className={`flex-1 h-11 rounded-md text-sm font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap ${
              favorite ? "bg-accent-500 text-background-50" : "bg-primary-500 text-background-50"
            }`}
          >
            <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
            {favorite ? "已收藏" : "收藏作品"}
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-24 lg:bottom-10 left-1/2 -translate-x-1/2 z-[70] px-4 h-10 rounded-md bg-foreground-900 text-background-50 text-sm flex items-center">
          {toast}
        </div>
      )}
    </div>
  );
}